import { getContactSupportLink, getCurrencyCode } from "@/features/branding";
import { downloadXlsxFile } from "@/features/custom_downloads";
import { logToolUsage } from "@/features/tracking";
import { downloadLargeSiteSummary, enqueueLargeSiteSummary } from "@/tools/risk-bands/large-site-screen/utils/stream";
import { AdaptationType } from "@/types/adaptation";
import { useAvailablePortfolioTypes } from "@/utils/hooks/useAvailablePortfolioTypes";
import { useKeys } from "@/utils/hooks/useKeys";
import * as Sentry from "@sentry/nextjs";
import { notification } from "antd";
import { useCallback, useState } from "react";
import { PortfolioExportJobSchema } from "../types";
import { getRegionTypeSuffix } from "../utils/regionType";
import { downloadPortfolioSummary, enqueuePortfolioSummary } from "../utils/stream";
import { buildAggregateSummaryXlsxFilename } from "../utils/string";

const MAX_DOWNLOAD_ATTEMPTS = 3;

export interface UseSummaryActionsResult {
  isGenerating: boolean;
  isRegenerating: boolean;
  isDownloading: boolean;
  generate: (schema: PortfolioExportJobSchema) => Promise<void>;
  regenerate: (schema: PortfolioExportJobSchema) => Promise<void>;
  download: (schema: PortfolioExportJobSchema, adaptationType?: AdaptationType) => Promise<void>;
}

export function useSummaryActions(): UseSummaryActionsResult {
  const [isGenerating, setIsGenerating] = useState(false);
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [key] = useKeys();
  const availablePortfolioTypes = useAvailablePortfolioTypes();

  const generate = useCallback(
    async (schema: PortfolioExportJobSchema) => {
      const tool = schema.isLargeSite ? "largeSite" : "portfolioInsights";

      setIsGenerating(true);
      if (!key.user) return;

      try {
        if (schema.isLargeSite) {
          await enqueueLargeSiteSummary(schema.id, schema.summaryId, schema.secret, schema.stamps, schema.metadata);
        } else {
          await enqueuePortfolioSummary(schema.id, schema.summaryId, schema.secret, schema.stamps, schema.regionType);
        }

        logToolUsage({
          id: `comprehensive.${tool}.summary`,
          count: schema.assets.totalCount,
          portfolioId: schema.id,
          type: `${schema.isLargeSite ? "large_site" : "portfolio_insights"}:summary`,
        });
      } catch (err) {
        const message = err instanceof Error ? err.message : "";
        notification.open({
          duration: 0,
          message: "Summary Failed",
          description: `Error while generating "${schema.name}". Please try again or restart the run. If the issue persists, ${getContactSupportLink()}.\n${message}`,
        });

        Sentry.captureException(`Summary Request Error: ${err}`, {
          tags: { section: "Summary Request" },
          extra: {
            userId: schema.userId,
            portfolioId: schema.id,
            groupId: schema.groupId,
            summaryId: schema.summaryId,
          },
        });
      } finally {
        setIsGenerating(false);
      }
    },
    [key.user]
  );

  const regenerate = useCallback(
    async (schema: PortfolioExportJobSchema) => {
      const tool = schema.isLargeSite ? "largeSite" : "portfolioInsights";

      setIsRegenerating(true);
      if (!key.user) return;

      try {
        // force summary regen by passing empty previous summary id
        if (schema.isLargeSite) {
          await enqueueLargeSiteSummary(schema.id, "", schema.secret, schema.stamps, schema.metadata);
        } else {
          await enqueuePortfolioSummary(schema.id, "", schema.secret, schema.stamps, schema.regionType);
        }

        logToolUsage({
          id: `comprehensive.${tool}.summary`,
          count: schema.assets.totalCount,
          portfolioId: schema.id,
          type: `${schema.isLargeSite ? "large_site" : "portfolio_insights"}:summary`,
        });
      } catch (err) {
        notification.open({
          duration: 0,
          message: "Summary Failed",
          description: `Error while generating "${schema.name}". Please try again or restart the run. If the issue persists, ${getContactSupportLink()}.`,
        });

        Sentry.captureException(`Summary Request Error: ${err}`, {
          tags: { section: "Summary Request" },
          extra: {
            userId: schema.userId,
            portfolioId: schema.id,
            groupId: schema.groupId,
            summaryId: schema.summaryId,
            message: err instanceof Error ? err.message : "",
          },
        });
      } finally {
        setIsRegenerating(false);
      }
    },
    [key.user]
  );

  const download = useCallback(
    async (schema: PortfolioExportJobSchema, adaptedType?: AdaptationType) => {
      setIsDownloading(true);
      const currencyCode = getCurrencyCode();

      const downloadSummaryWithRetries = async () => {
        for (let attempt = 1; attempt <= MAX_DOWNLOAD_ATTEMPTS; attempt++) {
          try {
            if (schema.isLargeSite) {
              return await downloadLargeSiteSummary(
                schema.id,
                schema.summaryId,
                schema.secret,
                schema.stamps,
                schema.metadata,
                currencyCode
              );
            } else {
              const settings = {
                ...(currencyCode ? { currency_code: currencyCode } : undefined),
                resilience_pathway: (adaptedType ?? "baseline") as AdaptationType,
              };

              return await downloadPortfolioSummary(schema.summaryId, settings);
            }
          } catch (err) {
            if (attempt === MAX_DOWNLOAD_ATTEMPTS) throw err;
          }
        }
        throw new Error(`Summary download failed after ${MAX_DOWNLOAD_ATTEMPTS} attempts`);
      };

      try {
        let buffer: ArrayBuffer | undefined;

        try {
          buffer = await downloadSummaryWithRetries();
        } catch (err) {
          Sentry.captureException(`Summary Download Stale Report: ${err}`, {
            tags: { section: "Summary Download" },
            extra: {
              userId: schema.userId,
              portfolioId: schema.id,
              groupId: schema.groupId,
              summaryId: schema.summaryId,
              attempts: MAX_DOWNLOAD_ATTEMPTS,
            },
          });

          if (schema.isLargeSite) {
            await enqueueLargeSiteSummary(schema.id, "", schema.secret, schema.stamps, schema.metadata);
          } else {
            await enqueuePortfolioSummary(schema.id, "", schema.secret, schema.stamps, schema.regionType);
          }

          notification.open({
            duration: 3,
            message: "Regenerating Summary",
            description: `Generating updated summary for "${schema.name}". This may take a few minutes.`,
          });
          return;
        }

        if (buffer) {
          const tool = schema.isLargeSite ? "largeSite" : "portfolioInsights";
          const filename = buildAggregateSummaryXlsxFilename({
            schemaName: schema.name,
            regionTypeSuffix: schema.isLargeSite ? "" : getRegionTypeSuffix(schema.regionType, availablePortfolioTypes),
            isLargeSite: schema.isLargeSite,
            adaptationType: adaptedType,
          });

          downloadXlsxFile(buffer, { filename });

          notification.success({
            duration: 3,
            message: "Success",
            description: `Your summary "${schema.name}" has been successfully downloaded.`,
          });

          logToolUsage({
            id: `comprehensive.${tool}.summary`,
            count: schema.assets.totalCount,
            portfolioId: schema.id,
            type: `${schema.isLargeSite ? "large_site" : "portfolio_insights"}:summary`,
          });
        }
      } catch (err) {
        const message = err instanceof Error ? err.message : "";
        notification.error({
          duration: 0,
          message: "Summary Failed",
          description: `There was a problem downloading "${schema.name}". Please try again. If the issue persists, contact support.\n${message}`,
        });

        Sentry.captureException(`Summary Download Error: ${err}`, {
          tags: { section: "Summary Download" },
          extra: {
            userId: schema.userId,
            portfolioId: schema.id,
            groupId: schema.groupId,
            summaryId: schema.summaryId,
          },
        });
      } finally {
        setIsDownloading(false);
      }
    },
    [availablePortfolioTypes]
  );

  return { isGenerating, isRegenerating, isDownloading, generate, regenerate, download };
}
